import React, { useState } from 'react';
import { X, Download, Eye, Edit, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../services/invoiceHelpers';
import { statusColors } from '../utils/invoiceConstants';
import invoiceService from '../../../services/invoiceService';

const InvoiceModal = ({ invoice, onClose, onEdit }) => {
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);

  if (!invoice) return null;

  const handleViewPDF = async (e) => {
    e.stopPropagation();
    setViewing(true);
    try {
      await invoiceService.viewPDF(
        invoice.franchisee_id,
        invoice.id
      );
    } catch (error) {
      alert('Failed to view PDF. Please try again.');
    } finally {
      setViewing(false);
    }
  };

  const handleDownloadPDF = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      await invoiceService.downloadPDF(
        invoice.franchisee_id,
        invoice.id,
        invoice.invoice_number
      );
    } catch (error) {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="relative p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div className="flex justify-between items-start">
            <h2 className="text-xl md:text-2xl font-bold text-white">
              Invoice {invoice.invoice_number}
            </h2>
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(invoice); }}
                  className="flex items-center gap-1.5 text-white hover:text-gray-200 transition-colors duration-200 px-3 py-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 z-10 relative text-sm font-medium"
                  type="button"
                  title="Edit Invoice"
                >
                  <Edit size={18} />
                  Edit
                </button>
              )}
              <button
                onClick={handleViewPDF}
                disabled={viewing}
                className="flex items-center gap-1.5 text-white hover:text-gray-200 transition-colors duration-200 px-3 py-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 z-10 relative text-sm font-medium disabled:opacity-50"
                type="button"
                title="View PDF"
              >
                {viewing ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                {viewing ? 'Loading...' : 'View'}
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="flex items-center gap-1.5 text-white hover:text-gray-200 transition-colors duration-200 px-3 py-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 z-10 relative text-sm font-medium disabled:opacity-50"
                type="button"
                title="Download PDF"
              >
                {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                {downloading ? 'Generating...' : 'Download'}
              </button>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20 z-10 relative"
                type="button"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Centered Status */}
          <div className="absolute inset-x-0 bottom-4 flex justify-center">
            <span className={`inline-block px-4 py-2 rounded-full text-lg font-semibold ${statusColors[invoice.status]} border border-white shadow-lg capitalize`}>
              {invoice.status}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">

        <div className="p-4 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-6">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-pest-blue">Invoice Details</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <div className="text-gray-900">{formatDate(invoice.invoice_date)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <div className="text-gray-900">{formatDate(invoice.due_date)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Technician</label>
                  <div className="text-gray-900">{invoice.technician_name || '-'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <div className="text-gray-900">
                    {invoice.class ? (
                      <>
                        <div className="font-medium">{invoice.class.split(':')[0]}</div>
                        {invoice.class.includes(':') && (
                          <div className="text-sm text-gray-600">{invoice.class.split(':')[1]}</div>
                        )}
                      </>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-pest-blue">Customer Information</h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <div className="text-gray-900">{invoice.customer_name}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="text-gray-900">{invoice.customer_email || '-'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(invoice.bill_addr_line1 || invoice.customer_name) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Invoice To:</label>
                      <div className="text-gray-900">
                        <div className="font-medium">{invoice.customer_name}</div>
                        {invoice.bill_company_name && (
                          <div className="text-sm text-gray-600">{invoice.bill_company_name}</div>
                        )}
                        {invoice.bill_addr_line1 && (
                          <div className="text-sm mt-1">
                            {invoice.bill_addr_line1}<br/>
                            {invoice.bill_addr_line2 && <>{invoice.bill_addr_line2}<br/></>}
                            {invoice.bill_addr_city} {invoice.bill_addr_state} {invoice.bill_addr_postal_code}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(invoice.ship_addr_line1 || invoice.treatment_address) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Address:</label>
                      <div className="text-gray-900">
                        {invoice.ship_customer_name && (
                          <div className="font-medium">{invoice.ship_customer_name}</div>
                        )}
                        {invoice.ship_company_name && (
                          <div className="text-sm text-gray-600">{invoice.ship_company_name}</div>
                        )}
                        {invoice.ship_addr_line1 ? (
                          <div className="text-sm mt-1">
                            {invoice.ship_addr_line1}<br/>
                            {invoice.ship_addr_line2 && <>{invoice.ship_addr_line2}<br/></>}
                            {invoice.ship_addr_city} {invoice.ship_addr_state} {invoice.ship_addr_postal_code}
                          </div>
                        ) : invoice.treatment_address ? (
                          <div className="text-sm mt-1">{invoice.treatment_address}</div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          {invoice.line_items && invoice.line_items.length > 0 && (
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Services & Products</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Service/Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rate
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoice.line_items.map((item, index) => (
                        <tr key={item.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm">
                            <div className="font-medium text-gray-900">
                              {item.product_service}
                            </div>
                            {item.service_date && (
                              <div className="text-xs text-gray-500 mt-1">
                                Service Date: {formatDate(item.service_date)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div className="max-w-xs">
                              {item.description ? (
                                <div className="whitespace-pre-wrap break-words">
                                  {item.description}
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">No description</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-gray-900">
                            {item.quantity || '1'}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-gray-900">
                            {formatCurrency(item.rate)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Financial Summary - Full Width */}
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-pest-blue">Financial Summary</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtotal</label>
                    <div className="text-lg font-semibold text-gray-900">{formatCurrency(invoice.subtotal)}</div>
                  </div>
                  <div className="text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">VAT</label>
                    <div className="text-lg font-semibold text-gray-900">{formatCurrency(invoice.tax_amount)}</div>
                  </div>
                  <div className="text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total</label>
                    <div className="text-lg font-semibold text-gray-900">{formatCurrency(invoice.total_amount)}</div>
                  </div>
                  <div className="text-center">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Balance Due</label>
                    <div className={`text-lg font-semibold ${invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(invoice.balance_due)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          {invoice.memo && (
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Notes</h3>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 leading-relaxed">{invoice.memo}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
